import { UNLIMITED_ENTITLEMENT_PROFILE_KEY } from "@intelligems/ig-types/dtos/billing/modular-catalog";

import type { BillingEntityCalloutViewModel } from "../types";
import type { BillingPlan, Chip } from "./types";

export const PRODUCT_VERSION_2026 = "2026_module_v1";
export const STATIC_MARKETING_ANNUAL_DISCOUNT_PERCENTAGE = 20;
export const MONTHLY_EQUIVALENT_PRICE_FREQUENCY = "/mo";

export const UNLIMITED_ID = UNLIMITED_ENTITLEMENT_PROFILE_KEY;
export const SMART_CONTENT_ID = "smart-content";
export const SMART_CHECKOUT_ID = "smart-checkout";
export const SMART_MERCHANDISING_ID = "smart-merchandising";
export const SMART_PRICING_LIMITED_ID = "smart-pricing-limited";
export const SMART_PRICING_UNLIMITED_ID = "smart-pricing-unlimited";
export const SMART_SHIPPING_ID = "smart-shipping";

export const SMART_PRICING_VARIANTS = new Set([
  SMART_PRICING_LIMITED_ID,
  SMART_PRICING_UNLIMITED_ID,
]);

export const MODULE_ORDER: string[] = [
  SMART_CONTENT_ID,
  SMART_MERCHANDISING_ID,
  SMART_PRICING_LIMITED_ID,
  SMART_PRICING_UNLIMITED_ID,
  SMART_SHIPPING_ID,
  SMART_CHECKOUT_ID,
];

export const SMART_PRICING_VARIANT_DISPLAY: Record<
  string,
  { name: string; description?: string }
> = {
  [SMART_PRICING_LIMITED_ID]: { name: "Limited" },
  [SMART_PRICING_UNLIMITED_ID]: { name: "Unlimited" },
};

export type PlanStaticConfig = Omit<
  Partial<BillingPlan>,
  "name" | "description"
> & {
  commitment: number;
  icon?: string;
  name: string;
  description: string;
};

export const PLAN_STATIC_CONFIG: Record<string, PlanStaticConfig> = {
  [UNLIMITED_ID]: {
    commitment: 3,
    icon: "gem",
    name: "Unlimited",
    description: "Get everything bundle",
    subtitle: "GET EVERYTHING BUNDLE",
    variant: "unlimited",
  },
};

export const PAUSE_PLAN_MONTHLY_PRICE_USD = 9;

export const EXPERT_SUPPORT_CALLOUT: BillingEntityCalloutViewModel = {
  features: [
    "Dedicated Customer Success manager",
    "Storefront audit and testing roadmap",
    "Live guided onboarding",
  ],
  title: "Expert support",
};

export type ModuleStaticConfig = {
  commitment: number;
  icon?: string;
  name: string;
  description: string;
  chips?: Chip[];
  subtitle?: string;
  variant?: "default" | "premium" | "unlimited";
  variantProductKeys?: string[];
  stripeProductNames?: string[];
};

export const MODULE_STATIC_CONFIG: Record<string, ModuleStaticConfig> = {
  [SMART_CONTENT_ID]: {
    commitment: 0,
    icon: "pencil-ruler",
    name: "Smart Content",
    description: "Test and personalize content",
    // "starter" is the legacy Starter tier's entitlement profile key; owning
    // it surfaces as the Smart Content module slot.
    variantProductKeys: ["smart-content-v1", "starter"],
  },
  [SMART_CHECKOUT_ID]: {
    commitment: 0,
    icon: "shapes",
    name: "Smart Checkout & Post-purchase",
    description: "Lift order value at checkout",
    stripeProductNames: ["Smart Checkout and Post-Purchase"],
    variantProductKeys: ["smart-checkout-and-postpurchase"],
  },
  [SMART_PRICING_LIMITED_ID]: {
    commitment: 3,
    icon: "circle-dollar-sign",
    name: "Smart Pricing",
    description: "Optimize prices and discounts",
    stripeProductNames: ["Smart Pricing Limited", "Smart Pricing T1"],
    variantProductKeys: ["smart-pricing-t1"],
  },
  [SMART_PRICING_UNLIMITED_ID]: {
    commitment: 3,
    icon: "circle-dollar-sign",
    name: "Smart Pricing Unlimited",
    description: "Optimize prices and discounts",
    stripeProductNames: ["Smart Pricing Unlimited", "Smart Pricing T2"],
    variantProductKeys: ["smart-pricing-t2"],
  },
  [SMART_MERCHANDISING_ID]: {
    commitment: 0,
    icon: "newspaper",
    name: "Smart Merchandising",
    description: "Boost cart value",
  },
  [SMART_SHIPPING_ID]: {
    commitment: 0,
    icon: "truck",
    name: "Smart Shipping",
    description: "Test rates and thresholds",
  },
};

export const PLAN_PROFILE_KEYS = new Set(Object.keys(PLAN_STATIC_CONFIG));
